"use client";

import { getGizmoSession } from "@/lib/gizmoSession";
import { useState, useEffect, Suspense } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  Box,
  Group,
  Button,
  Loader,
  Anchor,
  SimpleGrid,
  Badge,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconLock } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { PRICING } from "@/constants/pricing";
import { getOwnedServices, clearOwnedServices, type ServiceId, TOTAL_INDIVIDUAL_VALUE, SERVICE_PRICES } from "@/lib/ownedServices";
import { trackCheckoutStart, trackCtaClick } from "@/lib/analytics";
import { STYLE_PROFILE_V1 } from "@/lib/styleProfileFlag";


// Style tokens
const colors = {
  bg: "#FFFFFF",
  bgMuted: "#FAFAF8",
  text: "#1A1A1A",
  textMuted: "#555555",
  textSoft: "#888888",
  border: "#E8E5E0",
  success: "#2D7A4F",
};

interface CompletedDimensions {
  colorAnalysis: boolean;
  bodyType: boolean;
  draping: boolean;
  outfits: boolean;
}

interface BundleIntent {
  completed: CompletedDimensions;
  colorSeason?: string;
  kibbeType?: string;
}

// Rich service definitions with transformation-focused copy
const SERVICES: {
  id: keyof CompletedDimensions;
  serviceId: ServiceId;
  name: string;
  tagline: string;
  description: string;
  bullets: string[];
  price: number;
  image: string;
}[] = [
  {
    id: "colorAnalysis",
    serviceId: "color-analysis",
    name: "Color Analysis",
    tagline: "Your personalized color palette",
    description: "Discover which colors make you look vibrant and healthy — and which ones wash you out.",
    bullets: [
      "Your exact seasonal color type (12-season system)",
      "60+ colors in your personal palette",
      "Makeup, jewelry & hair color guidance",
    ],
    price: SERVICE_PRICES["color-analysis"],
    image: "/swatchPreviews/palette1.png",
  },
  {
    id: "bodyType",
    serviceId: "kibbe",
    name: "Body Type Analysis",
    tagline: "Know what actually flatters your shape",
    description: "Understand your bone structure and proportions so clothing cuts finally make sense.",
    bullets: [
      "Your Kibbe body type identified",
      "Best necklines, sleeves & silhouettes",
      "Fabrics and patterns that work for you",
    ],
    price: SERVICE_PRICES["kibbe"],
    image: "/kibbe/landing/comparison-table.png",
  },
  {
    id: "draping",
    serviceId: "draping",
    name: "Digital Draping",
    tagline: "See yourself in 144 colors",
    description: "AI-generated visualizations of you wearing different colors — so you can see what works.",
    bullets: [
      "144 color swatches draped on your photo",
      "Side-by-side comparisons",
      "Proof of what flatters vs. what doesn't",
    ],
    price: SERVICE_PRICES["draping"],
    image: "/drapeSamples/advancedPrice/demo2.png",
  },
  {
    id: "outfits",
    serviceId: "outfits",
    name: "Outfit Visualizations",
    tagline: "See yourself in 30+ flattering looks",
    description: "AI-generated outfit previews showing YOU in styles matched to your colors and body type.",
    bullets: [
      "30+ realistic outfit renders of you",
      "Casual, work, and elevated styles",
      "Matched to your palette & proportions",
    ],
    price: SERVICE_PRICES["outfits"],
    image: "/swatchPreviews/girlCasual1.png",
  },
];

function getBundlePrice(completed: CompletedDimensions): number {
  const completedCount = Object.values(completed).filter(Boolean).length;
  switch (completedCount) {
    case 0:
      return PRICING.STYLE_PROFILE_BUNDLE.FULL_PRICE;
    case 1:
      return PRICING.STYLE_PROFILE_BUNDLE.THREE_REMAINING_PRICE;
    case 2:
      return PRICING.STYLE_PROFILE_BUNDLE.TWO_REMAINING_PRICE;
    case 3:
      return PRICING.STYLE_PROFILE_BUNDLE.ONE_REMAINING_PRICE;
    default:
      return 0;
  }
}

function getBundleType(completed: CompletedDimensions): string {
  const completedCount = Object.values(completed).filter(Boolean).length;
  switch (completedCount) {
    case 0:
      return "style-profile-full";
    case 1:
      return "style-profile-3";
    case 2:
      return "style-profile-2";
    case 3:
      return "style-profile-1";
    default:
      return "style-profile-full";
  }
}

function BundleCheckoutContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [bundleIntent, setBundleIntent] = useState<BundleIntent | null>(null);

  useEffect(() => {
    const ownedServices = getOwnedServices();

    let colorSeason: string | undefined;
    let kibbeType: string | undefined;
    let completedFromSession: CompletedDimensions | undefined;

    // Try to read the bundleIntent from sessionStorage (set by upsell card)
    const stored = sessionStorage.getItem("bundleIntent");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        colorSeason = parsed.colorSeason;
        kibbeType = parsed.kibbeType;
        // Use the completed state from sessionStorage if available
        if (parsed.completed) {
          completedFromSession = parsed.completed;
        }
      } catch {
        // Ignore
      }
    }

    // Use sessionStorage completed if available, otherwise fall back to localStorage
    // This ensures the bundle page shows the same state as the upsell card
    const completed: CompletedDimensions = completedFromSession || {
      colorAnalysis: ownedServices.includes("color-analysis"),
      bodyType: ownedServices.includes("kibbe"),
      draping: ownedServices.includes("draping"),
      outfits: ownedServices.includes("outfits"),
    };

    setBundleIntent({ completed, colorSeason, kibbeType });
  }, []);

  const handleCheckout = async () => {
    if (!bundleIntent) return;

    setIsLoading(true);
    try {
      const bundleType = getBundleType(bundleIntent.completed);

      trackCheckoutStart("style-profile-bundle");
      const response = await axios.post("/api/stripe/createStripeCheckoutPage", {
          gizmoSession: getGizmoSession(),
        type: bundleType,
        bundleCompleted: bundleIntent.completed,
        colorSeason: bundleIntent.colorSeason,
        kibbeType: bundleIntent.kibbeType,
      });

      if (response.data.url) {
        // DON'T clear bundleIntent here - the wizard needs it after Stripe redirect
        // The wizard will clear it after processing starts
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      notifications.show({
        title: "Error",
        message: "Something went wrong. Please try again.",
        color: "red",
      });
      setIsLoading(false);
    }
  };

  if (!bundleIntent) {
    return (
      <Container size={1100} py={60}>
        {/* Server-rendered H1 for SEO - visible during loading */}
        <Title
          order={1}
          fz={{ base: 32, md: 44 }}
          fw={600}
          lh={1.15}
          c={colors.text}
          style={{ letterSpacing: "-0.025em" }}
          ta="center"
          mb="xl"
        >
          Complete Style Profile Bundle
        </Title>
        <Stack align="center" justify="center" h={200}>
          <Loader color="dark" size="md" />
        </Stack>
      </Container>
    );
  }

  const completedCount = Object.values(bundleIntent.completed).filter(Boolean).length;
  const remainingServices = SERVICES.filter((s) => !bundleIntent.completed[s.id]);
  const bundlePrice = getBundlePrice(bundleIntent.completed);
  const remainingValue = remainingServices.reduce((sum, s) => sum + s.price, 0);
  const savings = Math.round(remainingValue - bundlePrice);

  return (
    <Container size={1100} py={{ base: 40, md: 60 }}>
      <Stack gap={48}>
        {/* HERO SECTION */}
        <Box ta="center">
          {/* Progress indicator */}
          <Text fz="sm" c={colors.textSoft} tt="uppercase" style={{ letterSpacing: "0.05em" }} mb="md">
            {completedCount} of 4 complete
          </Text>

          <Title
            order={1}
            fz={{ base: 32, md: 44 }}
            fw={600}
            lh={1.15}
            c={colors.text}
            style={{ letterSpacing: "-0.025em" }}
            maw={800}
            mx="auto"
          >
            Your complete style profile — colors, silhouettes, and outfits that work together.
          </Title>

          <Text fz={{ base: 16, md: 18 }} c={colors.textMuted} mt="lg" maw={600} mx="auto" lh={1.6}>
            Upload one photo. Get your color season, your body type analysis, and outfit ideas designed for your combination.
          </Text>

          {/* Value proposition */}
          <Paper
            p="md"
            radius={12}
            mt="xl"
            maw={500}
            mx="auto"
            style={{
              background: colors.bgMuted,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Group justify="center" gap="lg">
              <Box ta="center">
                <Text fz="xs" c={colors.textSoft} tt="uppercase">
                  If bought separately
                </Text>
                <Text fz="xl" fw={500} td="line-through" c={colors.textSoft}>
                  ${remainingValue.toFixed(0)}
                </Text>
              </Box>
              <Text fz="xl" c={colors.textSoft}>→</Text>
              <Box ta="center">
                <Text fz="xs" c={colors.textSoft} tt="uppercase">
                  Bundle price
                </Text>
                <Text fz="xl" fw={600} c={colors.text}>
                  ${bundlePrice}
                </Text>
              </Box>
              <Badge size="lg" color="green" variant="light" radius="sm">
                Save ${savings}
              </Badge>
            </Group>
          </Paper>
        </Box>

        {/* SERVICE CARDS */}
        <Box>
          <Text fz={20} fw={600} c={colors.text} mb="lg" ta="center">
            Everything included in your profile
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            {SERVICES.map((service) => {
              const isCompleted = bundleIntent.completed[service.id];
              const isLocked = !isCompleted;

              return (
                <Paper
                  key={service.id}
                  radius={14}
                  style={{
                    background: isCompleted ? colors.bgMuted : colors.bg,
                    border: `1px solid ${colors.border}`,
                    opacity: isCompleted ? 0.7 : 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  {/* Content - Left */}
                  <Box p="lg" style={{ flex: 1 }}>
                    <Group gap="sm" mb="xs">
                      {isCompleted ? (
                        <Box
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            background: colors.success,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconCheck size={12} color="#fff" stroke={3} />
                        </Box>
                      ) : (
                        <Box
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            background: colors.text,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconLock size={11} color="#fff" stroke={2.5} />
                        </Box>
                      )}
                      <Text fz="md" fw={600} c={isCompleted ? colors.textMuted : colors.text}>
                        {service.name}
                      </Text>
                    </Group>

                    <Badge
                      size="sm"
                      variant="light"
                      radius="sm"
                      mb="sm"
                      styles={{
                        root: {
                          textTransform: "none",
                          background: isCompleted ? "rgba(45, 122, 79, 0.1)" : "rgba(0,0,0,0.06)",
                          color: isCompleted ? colors.success : colors.textMuted,
                        },
                      }}
                    >
                      {isCompleted ? "Already yours" : `$${service.price} value`}
                    </Badge>

                    <Text fz="sm" c={colors.textMuted} lh={1.5} mb="sm">
                      {service.description}
                    </Text>

                    <Stack gap={4}>
                      {service.bullets.map((bullet, i) => (
                        <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                          <Text c={isCompleted ? colors.textSoft : colors.success} fz="xs" style={{ lineHeight: 1.4 }}>
                            ✓
                          </Text>
                          <Text fz="xs" c={isCompleted ? colors.textSoft : colors.textMuted} lh={1.4}>
                            {bullet}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </Box>

                  {/* Preview image - Right */}
                  <Box
                    style={{
                      position: "relative",
                      width: 160,
                      minHeight: 160,
                      background: "#f5f5f5",
                      flexShrink: 0,
                    }}
                  >
                    <Image
                      src={service.image}
                      alt={service.name}
                      fill
                      style={{ objectFit: "contain" }}
                      unoptimized={service.image.endsWith(".gif")}
                    />
                  </Box>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Box>

        {/* PRICING CTA */}
        <Paper
          p={{ base: "lg", md: "xl" }}
          radius={16}
          style={{
            background: colors.bgMuted,
            border: `1px solid ${colors.border}`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" style={{ alignItems: "center" }}>
            <Box>
              <Text fz="sm" c={colors.textSoft} tt="uppercase" style={{ letterSpacing: "0.05em" }} mb="xs">
                {completedCount > 0 ? `${remainingServices.length} services remaining` : "Complete Style Profile"}
              </Text>
              <Group gap="md" align="baseline">
                <Title order={2} fz={42} fw={600} c={colors.text}>
                  ${bundlePrice}
                </Title>
                {savings > 0 && (
                  <Text fz="lg" c={colors.textSoft}>
                    <Text span td="line-through">${remainingValue.toFixed(0)}</Text>
                    {" "}
                    <Text span c={colors.success} fw={500}>Save ${savings}</Text>
                  </Text>
                )}
              </Group>
              <Text fz="sm" c={colors.textMuted} mt="sm">
                One photo. One complete style system.
              </Text>
            </Box>

            <Stack gap="sm">
              <Button
                size="lg"
                fullWidth
                radius={14}
                loading={isLoading}
                onClick={handleCheckout}
                styles={{
                  root: {
                    background: "linear-gradient(180deg, #2B2523 0%, #1A1816 100%)",
                    height: 56,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                    "&:hover": {
                      background: "linear-gradient(180deg, #3A3330 0%, #2B2523 100%)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
                    },
                  },
                  label: {
                    fontWeight: 600,
                    fontSize: 16,
                    letterSpacing: "-0.01em",
                  },
                }}
              >
                Get My Complete Style Profile
              </Button>

              <Group justify="center" gap="lg">
                <Text fz="xs" c={colors.textSoft}>
                  One-time purchase
                </Text>
                <Text fz="xs" c={colors.textSoft}>
                  Instant access
                </Text>
                <Text fz="xs" c={colors.textSoft}>
                  Private & secure
                </Text>
              </Group>
            </Stack>
          </SimpleGrid>
        </Paper>

        {/* HOW IT WORKS */}
        <Box>
          <Text fz={20} fw={600} c={colors.text} mb="lg" ta="center">
            How it works
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {[
              {
                num: "1",
                title: "Upload one photo",
                desc: "Good lighting, neutral background. We use it for all 4 analyses.",
              },
              {
                num: "2",
                title: "We analyze everything",
                desc: "Colors, body type, draping visualizations, and outfit renders — all generated together.",
              },
              {
                num: "3",
                title: "Get your complete profile",
                desc: "Instant access to your colors, silhouettes, and personalized outfit ideas.",
              },
            ].map((step) => (
              <Paper
                key={step.num}
                p="lg"
                radius={14}
                style={{
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: colors.text,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 12,
                  }}
                >
                  <Text fz="sm" fw={600} c="#fff">
                    {step.num}
                  </Text>
                </Box>
                <Text fw={500} c={colors.text} mb={4}>
                  {step.title}
                </Text>
                <Text fz="sm" c={colors.textMuted} lh={1.5}>
                  {step.desc}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>

          <Text fz="sm" c={colors.textMuted} ta="center" mt="lg">
            <Anchor fz="sm" c={colors.text} component={Link} href="/photo-tips">
              Photo tips →
            </Anchor>
          </Text>
        </Box>

        {/* SECONDARY CTA */}
        <Box ta="center" pt="md">
          <Text fz="sm" c={colors.textMuted}>
            Not ready for the full bundle?{" "}
            <Anchor fz="sm" c={colors.text} component={Link} href="/ai-color-analysis">
              Start with Color Analysis ($24.99)
            </Anchor>
          </Text>

          {/* Reset option - for users who had partial failures */}
          {completedCount > 0 && (
            <Text fz="xs" c={colors.textSoft} mt="md">
              Had an issue with a previous order?{" "}
              <Anchor
                fz="xs"
                c={colors.textSoft}
                style={{ textDecoration: "underline" }}
                onClick={() => {
                  clearOwnedServices();
                  sessionStorage.removeItem("bundleIntent");
                  window.location.reload();
                }}
              >
                Reset and start fresh
              </Anchor>
            </Text>
          )}
        </Box>
      </Stack>
    </Container>
  );
}

function StyleProfileLanding() {
  const router = useRouter();
  const t = useTranslations("styleProfileLanding");
  const tResult = useTranslations("aiColorAnalysis.result");

  const deliverables = [
    tResult("spRowSeason"),
    tResult("spRowDrapes"),
    tResult("spRowOutfits"),
    tResult("spRowHair"),
    tResult("spRowMakeup"),
    tResult("spRowAvoid"),
    tResult("spRowPdf"),
  ];

  const steps = [
    { num: "1", title: t("step1"), desc: "" },
    { num: "2", title: t("step2"), desc: t("step2Desc") },
    { num: "3", title: t("step3"), desc: "" },
  ];

  const handleStart = () => {
    trackCtaClick("sp_landing_cta");
    router.push("/ai-color-analysis");
  };

  return (
    <Container size={1100} py={{ base: 40, md: 60 }}>
      <Stack gap={48}>
        <Box ta="center">
          <Title
            order={1}
            fz={{ base: 32, md: 44 }}
            fw={600}
            lh={1.15}
            c={colors.text}
            style={{ letterSpacing: "-0.025em" }}
            maw={800}
            mx="auto"
          >
            {t("heroTitle")}
          </Title>
          <Text fz={{ base: 16, md: 18 }} c={colors.textMuted} mt="lg" maw={600} mx="auto" lh={1.6}>
            {t("heroSub")}
          </Text>
        </Box>

        <Box>
          <Text fz={20} fw={600} c={colors.text} mb="lg" ta="center">
            {t("includedTitle")}
          </Text>
          <Paper p={{ base: "lg", md: "xl" }} radius={14} style={{ background: colors.bgMuted, border: `1px solid ${colors.border}` }}>
            <Stack gap="sm">
              {deliverables.map((item, i) => (
                <Group key={i} gap="sm" wrap="nowrap" align="flex-start">
                  <IconCheck size={16} color={colors.success} style={{ marginTop: 3, flexShrink: 0 }} />
                  <Text fz="sm" c={colors.textMuted} lh={1.5}>{item}</Text>
                </Group>
              ))}
            </Stack>
          </Paper>
        </Box>

        <Box>
          <Text fz={20} fw={600} c={colors.text} mb="lg" ta="center">
            {t("howTitle")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {steps.map((step) => (
              <Paper key={step.num} p="lg" radius={14} style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: colors.text,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 12,
                  }}
                >
                  <Text fz="sm" fw={600} c="#fff">{step.num}</Text>
                </Box>
                <Text fw={500} c={colors.text} mb={4}>{step.title}</Text>
                {step.desc && <Text fz="sm" c={colors.textMuted} lh={1.5}>{step.desc}</Text>}
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        <Paper
          p={{ base: "lg", md: "xl" }}
          radius={16}
          style={{ background: colors.bgMuted, border: `1px solid ${colors.border}`, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
        >
          <Stack gap="md" align="center">
            <Text fz="sm" c={colors.textMuted}>{t("oneTime")}</Text>
            <Button
              size="lg"
              radius="md"
              rightSection={<IconArrowRight size={18} />}
              onClick={handleStart}
              styles={{
                root: {
                  height: 56,
                  minWidth: 280,
                  background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                  border: "none",
                  boxShadow: "0 4px 14px rgba(109, 40, 217, 0.3)",
                },
                label: { fontSize: 16, fontWeight: 600 },
              }}
            >
              {t("cta")}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

export default function Content() {
  if (STYLE_PROFILE_V1) {
    return <StyleProfileLanding />;
  }

  return (
    <Suspense
      fallback={
        <Container size={1100} py={60}>
          {/* Server-rendered H1 for SEO - always visible */}
          <Title
            order={1}
            fz={{ base: 32, md: 44 }}
            fw={600}
            lh={1.15}
            style={{ color: "#1A1A1A", letterSpacing: "-0.025em" }}
            ta="center"
            mb="xl"
          >
            Complete Style Profile Bundle
          </Title>
          <Stack align="center" justify="center" h={200}>
            <Loader color="dark" size="md" />
          </Stack>
        </Container>
      }
    >
      <BundleCheckoutContent />
    </Suspense>
  );
}
